// @ts-nocheck
import React, { useMemo } from "react";
import { dateFormatZone } from "./forecastUtil";

// The forecast table. An optional `title` names the channel when several tables are stacked: on its own line on small screens, and from lg up at the start of the meta row with the current time and units. It fills its host rather than measuring the viewport: the meta strip and the column heads stay put and only the rows scroll, so a dense forecast stays readable at any height.
const TabularData = ({ data, yaxis, interpolatedData, timeZone, title }) => {
  const headers = useMemo(() => {
    // Create a Set to gather unique keys from all data records
    const allKeys = new Set();
    data.forEach((item) => {
      Object.keys(item).forEach((key) => allKeys.add(key));
    });

    // Transform the keys into the desired header format
    return Array.from(allKeys).reduce((acc, curr) => {
      if (curr === "dateTime") {
        return { dateTime: "Date Time", ...acc };
      } else if (curr === "mean") {
        return { ...acc, mean: "Average" };
      } else {
        return { ...acc, [curr]: `${curr[0].toUpperCase()}${curr.slice(1)}` };
      }
    }, {});
  }, [data]);

  // A column is numeric only if every value it actually carries is one — so a measure right-aligns on its decimal point while a label column stays left, and an all-"-" column does not flip sides
  const numericColumns = useMemo(() => {
    const keys = Object.keys(headers).filter((key) => key !== "dateTime");
    return new Set(
      keys.filter((key) =>
        data.some((row) => Number.isFinite(Number(row[key])) && row[key] !== null && row[key] !== "") &&
        data.every(
          (row) =>
            row[key] === null ||
            row[key] === undefined ||
            row[key] === "" ||
            Number.isFinite(Number(row[key]))
        )
      )
    );
  }, [data, headers]);

  const currentTimeClipped = (newTimeDate) => {
    const { year, month, date, hour, minute, intTimeZone } = dateFormatZone(newTimeDate, timeZone);
    return `${date}-${month}-${year} ${hour}:${minute} ${intTimeZone}`;
  };

  const align = (key) => (numericColumns.has(key) ? "text-right tabular-nums" : "text-left");

  const renderRows = () => {
    let currentTimeBorder = false;
    return data.map((entry, i) => {
      // The one row the present moment falls inside gets the marker, matching the chart's now-line
      let nowMarker = "";
      if (i !== data.length - 1 && !currentTimeBorder) {
        const targetDate = new Date();
        const startDate = new Date(entry.dateTime);
        const endDate = new Date(data[i + 1].dateTime);
        if (targetDate >= startDate && targetDate <= endDate) {
          nowMarker = "!border-b-2 !border-b-eai-danger-solid";
          currentTimeBorder = true;
        }
      }

      return (
        <tr
          className={`border-b border-eai-border transition-colors odd:bg-eai-surface-sunken hover:bg-eai-surface-hover ${nowMarker}`}
          key={entry.dateTime}
        >
          {Object.keys(headers).map((headerKeys) => (
            <td
              className={`whitespace-nowrap px-3 py-1.5 font-inter text-eai-body-xs text-eai-ink ${align(
                headerKeys
              )}`}
              key={`${entry.dateTime}_${headerKeys}`}
            >
              {headerKeys === "dateTime"
                ? currentTimeClipped(new Date(entry[headerKeys]))
                : entry[headerKeys] ?? "-"}
            </td>
          ))}
        </tr>
      );
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-none flex-wrap items-baseline gap-x-5 gap-y-1 px-3 pb-2">
        {title && (
          <span className="w-full truncate font-outfit text-lg font-medium text-eai-ink lg:mr-3 lg:w-auto">{title}</span>
        )}
        <span className="inline-flex items-baseline gap-1.5">
          <span className="eai-meta-label">Current Time</span>
          <span className="font-outfit text-eai-control text-eai-ink">
            {currentTimeClipped(new Date())}
          </span>
        </span>
        {yaxis && (
          <span className="inline-flex items-baseline gap-1.5">
            <span className="eai-meta-label">Units</span>
            <span className="font-outfit text-eai-control text-eai-ink">{yaxis}</span>
          </span>
        )}
      </div>

      <div className="scrollbar-hover min-h-0 flex-1 overflow-auto">
        <table className="w-full border-separate border-spacing-0">
          {/* Sticky heads need their own ground, or the rows scroll through them */}
          <thead className="sticky top-0 z-10">
            <tr>
              {Object.keys(headers).map((key) => (
                <th
                  className={`whitespace-nowrap border-b border-eai-border bg-eai-surface-hover px-3 py-2 font-outfit text-eai-meta font-medium uppercase text-eai-ink-subtle ${align(
                    key
                  )}`}
                  key={key}
                >
                  {headers[key]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default TabularData;
